/**
 * HearthfireSkillEffect — Fire tree ember-path apotheosis: warmth pulse + sustained regen.
 *
 * A pulse of warmth across a 5-tile radius: 20% max HP healed instantly, enemies
 * catch a 4/s burn DoT for 5 seconds. For the next 60 seconds, every fire source
 * the player has created in line of sight heals the player 2% max HP every 3 seconds.
 *
 * **Qualifying fire sources:** `fire_zone` (Cultivate, Immolate), `fire_wall` (Shape),
 * and `bonfire_zone` (Bonfire). Smoke clouds do NOT qualify — they are not fire.
 * Line-of-sight is checked against each zone's center position; zones behind walls
 * do not contribute.
 *
 * **Activation:** Instant on press. Breaks hide. Very long cooldown.
 *
 * **Fizzle:** Standard mastery fizzle check. On fizzle, cooldown is consumed but
 * no heal, no burning, no aura window.
 *
 * **Aura window (60 seconds):**
 *   - Every 3000ms: enumerate fire zones in LoS via dep callback.
 *   - Each qualifying zone heals the player 2% max HP per tick.
 *   - With 3 fire zones + 1 bonfire = 4 sources × 2% = 8% per tick.
 *   - This is the highest sustained regen in the tree — the apotheosis ceiling.
 *
 * **Acid Burn interaction:** The burning applied to enemies is source-tagged
 * `'hearthfire'`; the Acid Burn +25% combo bonus is applied externally by
 * CombatHitResolver when both burning and poison are active on a target.
 *
 * @see fire-spec.md § Apotheosis: Hearthfire
 * @see ADR-064 (attunement shared systems architecture)
 * @see ADR-066 (source-tagged DoT coexistence)
 */

import type { SkillEffect } from '../../SkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface HearthfireDeps {
  /** Returns the player's current world position in pixels. */
  readonly getPlayerPosition: () => { x: number; y: number };

  /** Return entity IDs of all enemies within `radiusTiles` of (x, y). */
  readonly getEnemiesInRadius: (
    x: number,
    y: number,
    radiusTiles: number,
  ) => string[];

  /**
   * Apply a status effect (burning) to an enemy entity.
   * Source-tagged per ADR-066 for DoT coexistence.
   */
  readonly applyEnemyStatus: (
    entityId: string,
    statusId: string,
    opts: { durationMs: number; intensity: number; sourceSkillId: string },
  ) => void;

  /**
   * Apply a buff status to the player entity.
   * Used for the hearthfire_aura 60-second window.
   */
  readonly applyPlayerStatus: (
    statusId: string,
    opts: { durationMs: number; sourceSkillId: string },
  ) => void;

  readonly player: {
    readonly maxHp: number;
    heal(amount: number): void;
  };

  /**
   * Returns positions of all active fire zones (fire_zone, fire_wall, bonfire_zone)
   * that the player currently has line of sight to.
   *
   * Game layer: ZoneSystem.getAll() filtered by type + Bresenham LoS check
   * against zone center positions.
   * Test layer: returns a pre-configured array.
   */
  readonly getFireZonesInLineOfSight: () => ReadonlyArray<{
    id: string;
    x: number;
    y: number;
  }>;

  /** Break stealth / hide state on activation. */
  readonly breakHide: () => void;

  /** Returns true if activation succeeds (false = fizzle). */
  readonly rollSkillSuccess: (skillId: string) => boolean;
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface HearthfireConfig {
  /** Cooldown in ms after activation. Very long tier. */
  readonly cooldownMs: number;
  /** AoE pulse radius in tiles. */
  readonly pulseRadiusTiles: number;
  /** Fraction of max HP healed instantly on activation. */
  readonly instantHealPercent: number;
  /** Burning DoT intensity applied to enemies in the pulse. */
  readonly burningIntensity: number;
  /** Burning DoT duration in ms. */
  readonly burningDurationMs: number;
  /** Duration of the regen aura window in ms. */
  readonly auraDurationMs: number;
  /** Interval between regen ticks during the aura window in ms. */
  readonly regenTickMs: number;
  /** Fraction of max HP healed per fire source per regen tick. */
  readonly regenPerSourcePercent: number;
}

const DEFAULT_CONFIG: HearthfireConfig = {
  cooldownMs: 4000,              // very_long cooldown tier
  pulseRadiusTiles: 5,           // spec: 5-tile radius
  instantHealPercent: 0.2,       // spec: 20% max HP
  burningIntensity: 4,           // spec: 4/s burn DoT
  burningDurationMs: 5000,       // spec: 5 seconds
  auraDurationMs: 60000,         // spec: 60-second window
  regenTickMs: 3000,             // spec: every 3 seconds
  regenPerSourcePercent: 0.02,   // spec: 2% max HP per source
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class HearthfireSkillEffect implements SkillEffect {
  readonly skillId = 'hearthfire';
  readonly type = 'active' as const;
  readonly activationMode = 'instant' as const;
  readonly failLabel = 'FIZZLE';

  private readonly _deps: HearthfireDeps;
  private readonly _config: HearthfireConfig;

  // State
  private _cooldownRemaining = 0;
  private _auraRemaining = 0;
  private _isAuraActive = false;
  private _regenAccumulator = 0;

  // Stats (for scenario assertions)
  private _activations = 0;
  private _fizzles = 0;
  private _totalInstantHeal = 0;
  private _totalRegenHeal = 0;
  private _regenTicks = 0;

  constructor(deps: HearthfireDeps, config: Partial<HearthfireConfig> = {}) {
    this._deps = deps;
    this._config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- Accessors ---

  get cooldown(): number { return this._cooldownRemaining; }
  get activations(): number { return this._activations; }
  get fizzles(): number { return this._fizzles; }
  get totalInstantHeal(): number { return this._totalInstantHeal; }
  get totalRegenHeal(): number { return this._totalRegenHeal; }
  get regenTicks(): number { return this._regenTicks; }
  get auraRemaining(): number { return this._auraRemaining; }

  // --- SkillEffect interface ---

  canActivate(): boolean {
    return this._cooldownRemaining <= 0 && !this._isAuraActive;
  }

  activate(): boolean {
    if (!this.canActivate()) return false;

    // Fizzle check — cooldown consumed even on fizzle.
    if (!this._deps.rollSkillSuccess('hearthfire')) {
      this._fizzles++;
      this._cooldownRemaining = this._config.cooldownMs;
      return false;
    }

    this._deps.breakHide();

    const pos = this._deps.getPlayerPosition();

    // 1. Instant heal: 20% max HP.
    const instantHeal = Math.round(
      this._deps.player.maxHp * this._config.instantHealPercent,
    );
    if (instantHeal > 0) {
      this._deps.player.heal(instantHeal);
      this._totalInstantHeal += instantHeal;
    }

    // 2. Apply burning to all enemies in 5-tile radius.
    const enemies = this._deps.getEnemiesInRadius(
      pos.x,
      pos.y,
      this._config.pulseRadiusTiles,
    );
    for (const enemyId of enemies) {
      this._deps.applyEnemyStatus(enemyId, 'burning', {
        durationMs: this._config.burningDurationMs,
        intensity: this._config.burningIntensity,
        sourceSkillId: 'hearthfire',
      });
    }

    // 3. Apply hearthfire_aura buff to player.
    this._deps.applyPlayerStatus('hearthfire_aura', {
      durationMs: this._config.auraDurationMs,
      sourceSkillId: 'hearthfire',
    });

    // 4. Start the aura window.
    this._isAuraActive = true;
    this._auraRemaining = this._config.auraDurationMs;
    this._regenAccumulator = 0;

    this._cooldownRemaining = this._config.cooldownMs;
    this._activations++;
    return true;
  }

  deactivate(): void {
    if (!this._isAuraActive) return;
    this._isAuraActive = false;
    this._auraRemaining = 0;
    this._regenAccumulator = 0;
  }

  isActive(): boolean { return this._isAuraActive; }

  update(delta: number): void {
    // Tick cooldown.
    if (this._cooldownRemaining > 0) {
      this._cooldownRemaining = Math.max(0, this._cooldownRemaining - delta);
    }

    if (!this._isAuraActive) return;

    // Aura duration countdown.
    this._auraRemaining -= delta;
    if (this._auraRemaining <= 0) {
      this._isAuraActive = false;
      this._auraRemaining = 0;
      this._regenAccumulator = 0;
      return;
    }

    // Regen tick accumulator.
    this._regenAccumulator += delta;
    while (this._regenAccumulator >= this._config.regenTickMs) {
      this._regenAccumulator -= this._config.regenTickMs;
      this._applyRegenTick();
    }
  }

  destroy(): void {
    this._isAuraActive = false;
    this._auraRemaining = 0;
    this._cooldownRemaining = 0;
    this._regenAccumulator = 0;
  }

  // ---------------------------------------------------------------------------
  // Internal — regen tick
  // ---------------------------------------------------------------------------

  private _applyRegenTick(): void {
    const fireZones = this._deps.getFireZonesInLineOfSight();
    const sourceCount = fireZones.length;

    if (sourceCount === 0) return;

    const healAmount = Math.round(
      this._deps.player.maxHp * this._config.regenPerSourcePercent * sourceCount,
    );

    if (healAmount > 0) {
      this._deps.player.heal(healAmount);
      this._totalRegenHeal += healAmount;
    }

    this._regenTicks++;
  }
}
